import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog, F0Select, F0Text } from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'

import { countryName } from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

type RateNode = {
  id: string
  amountFullCents: number | string
  amountPartialCents: number | string
  amountOvernightCents: number | string
  currency: string
  destinationCountry: string
  locality?: string | null
  activeCompanyPerDiemRate?: {
    id?: string | null
    amountFullCents?: number | string | null
    amountPartialCents?: number | string | null
    amountOvernightCents?: number | string | null
    validFrom?: string
    validTo?: string | null
  } | null
}

type OverrideMode = 'multiplier' | 'percentage' | 'bonus'

type ValidationError =
  | 'multiplier_too_low'
  | 'percentage_too_low'
  | 'bonus_too_low'
  | 'bonus_not_whole_number'

interface Props {
  selectedRates: RateNode[]
  onClose: () => void
  onConfirm: (payload: { overrideMode: OverrideMode; overrideValue: number }) => Promise<void>
}

const MODE_CONFIG: Record<
  OverrideMode,
  {
    optionLabel: (currency: string) => string
    amountLabel: (currency: string) => string
    placeholder: () => string
  }
> = {
  bonus: {
    optionLabel: (currency) =>
      i18n.t('expenses_v2.settings.rate_configurations.override_modal.mode_options.bonus', {
        currency,
      }),
    amountLabel: (currency) =>
      i18n.t('expenses_v2.settings.rate_configurations.override_modal.input_labels.bonus', {
        currency,
      }),
    placeholder: () =>
      i18n.t('expenses_v2.settings.rate_configurations.override_modal.placeholders.amount'),
  },
  percentage: {
    optionLabel: () =>
      i18n.t('expenses_v2.settings.rate_configurations.override_modal.mode_options.percentage'),
    amountLabel: () =>
      i18n.t('expenses_v2.settings.rate_configurations.override_modal.input_labels.percentage'),
    placeholder: () =>
      i18n.t('expenses_v2.settings.rate_configurations.override_modal.placeholders.amount'),
  },
  multiplier: {
    optionLabel: () =>
      i18n.t('expenses_v2.settings.rate_configurations.override_modal.mode_options.multiplier'),
    amountLabel: () =>
      i18n.t('expenses_v2.settings.rate_configurations.override_modal.input_labels.multiplier'),
    placeholder: () =>
      i18n.t('expenses_v2.settings.rate_configurations.override_modal.placeholders.multiplier'),
  },
}

const OVERRIDE_MODES: OverrideMode[] = ['multiplier', 'percentage', 'bonus']

const MODE_SELECT_OPTIONS = OVERRIDE_MODES.map((mode) => ({
  value: mode,
}))

const isOverrideMode = (value: string): value is OverrideMode =>
  OVERRIDE_MODES.some((mode) => mode === value)

const destinationLabel = (rate: RateNode) => {
  const region = countryName(rate.destinationCountry, i18n.locale)
  const label = rate.locality ? `${region} - ${rate.locality}` : region

  return label
}

const dialogDescription = (rates: RateNode[]) => {
  const [firstRate] = rates
  const remainingCount = rates.length - 1

  if (!firstRate) {
    return i18n.t('expenses_v2.settings.rate_configurations.override_modal.description_empty')
  }

  if (remainingCount <= 0) {
    return i18n.t('expenses_v2.settings.rate_configurations.override_modal.description_single', {
      destination: destinationLabel(firstRate),
    })
  }

  return i18n.t('expenses_v2.settings.rate_configurations.override_modal.description_multiple', {
    destination: destinationLabel(firstRate),
    count: remainingCount,
  })
}

const computePreviewAmount = (
  officialAmount: number,
  overrideMode: OverrideMode,
  overrideValue: number
) => {
  switch (overrideMode) {
    case 'multiplier':
      return Math.round(officialAmount * overrideValue)
    case 'percentage':
      return Math.round((officialAmount * (100 + overrideValue)) / 100)
    case 'bonus':
      return officialAmount + Math.round(overrideValue)
  }
}

const previewDelta = (
  overrideMode: OverrideMode,
  overrideValue: number,
  currency: string | undefined
) => {
  switch (overrideMode) {
    case 'bonus':
      return `+${amountWithCurrency(Math.round(overrideValue * 100), currency ?? 'EUR', i18n.locale)}`
    case 'percentage':
      return `+${overrideValue}%`
    case 'multiplier':
      return `x${overrideValue}`
  }
}

const validateOverrideValue = (
  overrideMode: OverrideMode,
  overrideValue: number | undefined
): ValidationError | null => {
  if (overrideValue == null) return null

  switch (overrideMode) {
    case 'multiplier':
      return overrideValue > 1 ? null : 'multiplier_too_low'
    case 'percentage':
      return overrideValue > 0 ? null : 'percentage_too_low'
    case 'bonus':
      if (overrideValue <= 0) return 'bonus_too_low'
      return Number.isInteger(overrideValue * 100) ? null : 'bonus_not_whole_number'
  }
}

const validationMessage = (validationError: ValidationError | null) => {
  switch (validationError) {
    case 'multiplier_too_low':
      return i18n.t(
        'expenses_v2.settings.rate_configurations.override_modal.errors.multiplier_too_low'
      )
    case 'percentage_too_low':
      return i18n.t(
        'expenses_v2.settings.rate_configurations.override_modal.errors.percentage_too_low'
      )
    case 'bonus_too_low':
      return i18n.t('expenses_v2.settings.rate_configurations.override_modal.errors.bonus_too_low')
    case 'bonus_not_whole_number':
      return i18n.t(
        'expenses_v2.settings.rate_configurations.override_modal.errors.bonus_not_whole_number'
      )
    case null:
      return null
  }
}

const OverridePerDiemRateModal = ({ selectedRates, onClose, onConfirm }: Props) => {
  const [overrideMode, setOverrideMode] = React.useState<OverrideMode>('bonus')
  const [overrideValue, setOverrideValue] = React.useState<number | undefined>(undefined)
  const [loading, setLoading] = React.useState(false)

  const selectedRate = selectedRates[0]
  const previewCurrency = selectedRate?.currency ?? 'EUR'
  const config = MODE_CONFIG[overrideMode]
  const amountLabel = config.amountLabel(previewCurrency)
  const inputPlaceholder = config.placeholder()
  const modeSelectOptions = MODE_SELECT_OPTIONS.map((option) => ({
    ...option,
    label: MODE_CONFIG[option.value].optionLabel(previewCurrency),
  }))
  const hasReplacementWarning = selectedRates.some((rate) => rate.activeCompanyPerDiemRate)
  const mutationOverrideValue =
    overrideMode === 'bonus' ? Math.round((overrideValue ?? 0) * 100) : (overrideValue ?? 0)
  const validationError = validateOverrideValue(overrideMode, overrideValue)
  const validationErrorMessage = validationMessage(validationError)
  const modalDescription = dialogDescription(selectedRates)

  const previewFullDay = computePreviewAmount(
    Number(selectedRate?.amountFullCents ?? 0),
    overrideMode,
    mutationOverrideValue
  )
  const previewHalfDay = computePreviewAmount(
    Number(selectedRate?.amountPartialCents ?? 0),
    overrideMode,
    mutationOverrideValue
  )
  const previewOvernight = computePreviewAmount(
    Number(selectedRate?.amountOvernightCents ?? 0),
    overrideMode,
    mutationOverrideValue
  )
  const previewSummary =
    overrideValue == null
      ? null
      : {
          delta: previewDelta(overrideMode, overrideValue, selectedRate?.currency),
          description:
            selectedRates.length > 1
              ? i18n.t(
                  'expenses_v2.settings.rate_configurations.override_modal.preview_description_multiple',
                  {
                    destination: selectedRate ? destinationLabel(selectedRate) : '',
                  }
                )
              : i18n.t(
                  'expenses_v2.settings.rate_configurations.override_modal.preview_description_single',
                  {
                    destination: selectedRate ? destinationLabel(selectedRate) : '',
                  }
                ),
          rows: [
            {
              label: i18n.t(
                'expenses_v2.settings.rate_configurations.datatable.fields.amount_full'
              ),
              official: amountWithCurrency(
                Number(selectedRate?.amountFullCents ?? 0),
                previewCurrency,
                i18n.locale
              ),
              preview: amountWithCurrency(previewFullDay, previewCurrency, i18n.locale),
            },
            {
              label: i18n.t(
                'expenses_v2.settings.rate_configurations.datatable.fields.amount_partial'
              ),
              official: amountWithCurrency(
                Number(selectedRate?.amountPartialCents ?? 0),
                previewCurrency,
                i18n.locale
              ),
              preview: amountWithCurrency(previewHalfDay, previewCurrency, i18n.locale),
            },
            {
              label: i18n.t(
                'expenses_v2.settings.rate_configurations.datatable.fields.amount_overnight'
              ),
              official: amountWithCurrency(
                Number(selectedRate?.amountOvernightCents ?? 0),
                previewCurrency,
                i18n.locale
              ),
              preview: amountWithCurrency(previewOvernight, previewCurrency, i18n.locale),
            },
          ],
        }

  const handleConfirm = async () => {
    if (overrideValue == null || validationError != null) return

    setLoading(true)
    try {
      await onConfirm({ overrideMode, overrideValue: mutationOverrideValue })
    } finally {
      setLoading(false)
    }
  }

  const handleNumberChange = (value: unknown) => {
    if (value === null || value === '') {
      setOverrideValue(undefined)
      return
    }

    if (typeof value === 'number') {
      setOverrideValue(value)
      return
    }

    if (typeof value === 'string') {
      if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
        const parsedValue = Number(value)
        setOverrideValue(Number.isNaN(parsedValue) ? undefined : parsedValue)
      }
    }
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      illustration="document-edit"
      title={i18n.t('expenses_v2.settings.rate_configurations.override_modal.title')}
      description={modalDescription}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('expenses_v2.settings.rate_configurations.override_modal.confirm'),
        onClick: handleConfirm,
        loading,
        disabled: overrideValue == null || validationError != null,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box flexDirection="column" gap="s24">
        <Box flexDirection="column" gap="s12">
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s16">
            <F0Text variant="label" content={amountLabel} style={{ whiteSpace: 'nowrap' }} />
            <Box style={{ width: 'fit-content', flexShrink: '0' }}>
              <F0Select<OverrideMode>
                options={modeSelectOptions}
                value={overrideMode}
                onChange={(value: OverrideMode) => {
                  if (isOverrideMode(value)) {
                    setOverrideMode(value)
                  }
                }}
              />
            </Box>
          </Box>

          <NumberInput
            hideLabel
            locale={i18n.locale}
            label={amountLabel}
            value={overrideValue ?? null}
            placeholder={inputPlaceholder}
            units={overrideMode === 'bonus' ? previewCurrency : undefined}
            size="md"
            onChange={handleNumberChange}
          />

          {validationErrorMessage ? (
            <F0Text variant="description" content={validationErrorMessage} color="negative400" />
          ) : null}

          {hasReplacementWarning ? (
            <F0Alert
              variant="warning"
              title={i18n.t(
                'expenses_v2.settings.rate_configurations.override_modal.replacement_warning'
              )}
              description=""
            />
          ) : null}

          {previewSummary ? (
            <Box
              padding="s16"
              flexDirection="column"
              gap="s12"
              style={{
                background: 'var(--fill-secondary)',
                border: '1px solid var(--border-default)',
                borderRadius: '16px',
              }}
            >
              <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s12">
                <Box flexDirection="column" gap="s4">
                  <F0Text
                    variant="label"
                    content={i18n.t(
                      'expenses_v2.settings.rate_configurations.override_modal.preview_title'
                    )}
                  />
                  <F0Text variant="description" content={previewSummary.description} />
                </Box>
                <Box style={{ flexShrink: '0' }}>
                  <F0Text variant="description" content={previewSummary.delta} color="grey500" />
                </Box>
              </Box>

              <Box flexDirection="column" gap="s8">
                {previewSummary.rows.map((row) => (
                  <Box
                    key={row.label}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(112px, 1fr) max-content max-content max-content',
                      columnGap: '8px',
                      alignItems: 'baseline',
                    }}
                  >
                    <F0Text variant="description" content={row.label} />
                    <Box style={{ textAlign: 'right' }}>
                      <F0Text variant="description" content={row.official} />
                    </Box>
                    <Box style={{ textAlign: 'center' }}>
                      <F0Text variant="description" content="->" color="grey300" />
                    </Box>
                    <Box style={{ textAlign: 'right' }}>
                      <F0Text variant="label" content={row.preview} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </F0Dialog>
  )
}

export default OverridePerDiemRateModal
